import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../Config/config";
import { useTheme } from "../ThemeContext";
import { useAuth } from './AuthContext';
import styled from 'styled-components';
import Lottie from "react-lottie";
import truckAnimation from '../Animations/truck.json';
import offerAnimation from '../Animations/offer.json';

const DetailedPackageView = () => {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { themes, currentTheme } = useTheme() || {};
  const theme = themes?.[currentTheme] || {};
  const { user } = useAuth();

  const formatPrice = (price) => {
    if (!price) return '0';
    return String(price).trim();
  };

  useEffect(() => {
    const fetchPackageDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const packageDoc = await getDoc(doc(db, "packages", id));
        
        if (packageDoc.exists()) {
          const data = packageDoc.data();
          setPkg({
            id: packageDoc.id,
            conditions: Array.isArray(data.conditions) ? data.conditions : [],
            inclusions: Array.isArray(data.inclusions) ? data.inclusions : [],
            propertyInclusions: Array.isArray(data.propertyInclusions) ? data.propertyInclusions : [],
            specialBenefits: Array.isArray(data.specialBenefits) ? data.specialBenefits : [],
            // duration: String(data.duration || ''),
            duration: String(data.duration || '') ? `${String(data.duration || '')} Years` : '',
            feature1: String(data.feature1 || ''),
            feature2: String(data.feature2 || ''),
            members: String(data.members || ''),
            membershipCost: String(data.membershipCost || ''),
            name: String(data.name || ''),
            price: String(data.price || ''),
            actualPrice: formatPrice(data.price),
            type: String(data.type || ''),
            additionalOffers: String(data.additionalOffers || '')
          });
        } else {
          setError("Package not found");
        }
      } catch (err) {
        console.error("Error fetching package details:", err);
        setError("Failed to load package details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPackageDetails();
  }, [id]);

  const lottieOptions = {
    loop: true,
    autoplay: true,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice"
    }
  };

  const handleBuyNow = () => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', `/packagedetails/${id}`);
      sessionStorage.setItem('packageDetails', JSON.stringify({
        ...pkg,
        price: pkg.actualPrice
      }));
      navigate('/login');
    } else {
      navigate('/details', {
        state: {
          packageDetails: {
            ...pkg,
            price: pkg.actualPrice
          }
        }
      });
    }
  };

  if (loading) {
    return <CenteredContainer><LoadingMessage>Loading package details...</LoadingMessage></CenteredContainer>;
  }

  if (error) {
    return <CenteredContainer><ErrorMessage>{error}</ErrorMessage></CenteredContainer>;
  }

  if (!pkg) return null;

  const renderList = (items, containerStyle) => {
    if (!items || items.length === 0) return null;
    return (
      <ListContainer className={containerStyle}>
        {items.map((item, index) => (
          <ListItem key={index}>
            <CheckMark>✅</CheckMark> {item}
          </ListItem>
        ))}
      </ListContainer>
    );
  };

  return (
    <Container>
      <ScrollContainer>
        <Content>
          <Card>
            <LottieWrapper>
              <Lottie options={{...lottieOptions, animationData: truckAnimation}} height={200} width={200} />
            </LottieWrapper>
            <CardTitle>{pkg.name}</CardTitle>
            <PriceInfoContainer>
              <PriceInfo>Price - Rs. {pkg.actualPrice}</PriceInfo>
              <PriceInfo>Membership Cost - Rs. {pkg.membershipCost}</PriceInfo>
              <PriceInfo>Validity - {pkg.duration}</PriceInfo>
            </PriceInfoContainer>
          </Card>

          <FeatureText>{pkg.feature1}</FeatureText>
          <FeatureText>{pkg.feature2}</FeatureText>

          {pkg.inclusions?.length > 0 && (
            <>
              <SectionTitle>INCLUDES</SectionTitle>
              {renderList(pkg.inclusions, 'inclusions')}
            </>
          )}

          {pkg.propertyInclusions?.length > 0 && (
            <>
              <SectionTitle></SectionTitle>
              {renderList(pkg.propertyInclusions, 'property-inclusions')}
            </>
          )}

          {pkg.specialBenefits?.length > 0 && (
            <>
              <SectionTitle>SPECIAL BENEFITS</SectionTitle>
              {renderList(pkg.specialBenefits, 'special-benefits')}
            </>
          )}

          {pkg.conditions?.length > 0 && (
            <>
              <SectionTitle>CONDITIONS APPLY</SectionTitle>
              {renderList(pkg.conditions, 'conditions')}
            </>
          )}

          {pkg.additionalOffers && (
            <>
              <SectionTitle>Additional Offers</SectionTitle>
              <OfferContainer>
                <OfferLottieContainer>
                  <Lottie 
                    options={{...lottieOptions, animationData: offerAnimation}}
                    height={100}
                    width={100}
                  />
                </OfferLottieContainer>
                <OfferTextContainer>
                  <OfferText>{pkg.additionalOffers}</OfferText>
                </OfferTextContainer>
              </OfferContainer>
            </>
          )}

          <BuyButton onClick={handleBuyNow}>
            <ButtonText>CONTINUE</ButtonText>
          </BuyButton>
        </Content>
      </ScrollContainer>
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #f8f8f8;
  display: flex;
  flex-direction: column;
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 15vh 0 15vh;
  margin: -15vh 0 -15vh;
`;

const Content = styled.div`
  padding: 15vh 0 15vh;
`;

const CenteredContainer = styled(Container)`
  justify-content: center;
  align-items: center;
`;

const LoadingMessage = styled.div`
  font-family: 'Poppins-Medium', sans-serif;
`;

const ErrorMessage = styled(LoadingMessage)`
  color: red;
`;

const Card = styled.div`
  background-color: ${props => props.theme.primary || '#441B77'};
  margin: 15px;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const LottieWrapper = styled.div`
  height: 200px;
  width: 200px;
  margin: 0 auto;
`;

const CardTitle = styled.h2`
  font-family: 'Poppins-Bold', sans-serif;
  text-align: center;
  color: white;
  margin: 15px 0;
`;

const PriceInfoContainer = styled.div`
  margin-top: 10px;
`;

const PriceInfo = styled.p`
  font-family: 'Poppins-SemiBold', sans-serif;
  color: white;
  margin: 5px 0;
`;

const FeatureText = styled.p`
  margin: 10px 20px;
  font-family: 'Poppins-Medium', sans-serif;
  color: black;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.primary || '#441B77'};
  font-family: 'Poppins-SemiBold', sans-serif;
  font-size: 18px;
  text-align: center;
  margin: 30px 0 10px;
`;

const ListContainer = styled.div`
  margin: 0 10px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 6px;

  &.inclusions {
    background-color: #fff59e;
  }

  &.property-inclusions {
    background-color: #a7dca5;
  }

  &.special-benefits {
    background-color: #b4d9f8;
  }

  &.conditions {
    background-color: #ffebf0;
  }
`;

const ListItem = styled.p`
  margin: 10px 20px;
  font-family: 'Poppins-Medium', sans-serif;
  color: black;
  display: flex;
  align-items: center;
`;

const CheckMark = styled.span`
  margin-right: 10px;
`;

const OfferContainer = styled.div`
  background-color: #a7dca5;
  margin: 0 10px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  display: flex;
  flex-direction: row;
`;

const OfferLottieContainer = styled.div`
  height: 100px;
  width: 100px;
`;

const OfferTextContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 10px;
`;

const OfferText = styled.p`
  font-family: 'Poppins-Medium', sans-serif;
  color: black;
  font-size: 14px;
`;

const BuyButton = styled.button`
  background-color: ${props => props.theme.primary || '#441B77'};
  width: calc(100% - 20px);
  margin: 20px 10px 0;
  padding: 15px 0;
  border-radius: 5px;
  border: none;
  cursor: pointer;
`;

const ButtonText = styled.span`
  font-family: 'Poppins-SemiBold', sans-serif;
  color: white;
  font-size: 16px;
`;

export default DetailedPackageView;